// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { shortId } from '../../lib/format';
import { IdTag } from '../../ui/IdTag';
import { BRAND } from '../../lib/brand';
import { Gate } from '../../ui/Gate';

const PERMS = [
  { id: 'events.view',       group: 'Events',  label: 'View all events' },
  { id: 'events.edit',       group: 'Events',  label: 'Edit event configuration' },
  { id: 'events.publish',    group: 'Events',  label: 'Publish & unpublish events' },
  { id: 'events.cancel',     group: 'Events',  label: 'Cancel events (requires dual approval)' },
  { id: 'sales.pause',       group: 'Sales',   label: 'Pause sales · per event' },
  { id: 'sales.pause.all',   group: 'Sales',   label: 'Platform-wide kill switch' },
  { id: 'orders.refund',     group: 'Orders',  label: 'Issue refunds' },
  { id: 'orders.comp',       group: 'Orders',  label: 'Issue complimentary tickets' },
  { id: 'orders.manual',     group: 'Orders',  label: 'Approve manual orders' },
  { id: 'payouts.approve',   group: 'Finance', label: 'Approve payouts' },
  { id: 'payouts.override',  group: 'Finance', label: 'Override hold periods' },
  { id: 'finance.fees',      group: 'Finance', label: 'Edit fee schedules' },
  { id: 'kyc.review',        group: 'Risk',    label: 'Review KYC documents' },
  { id: 'risk.suspend',      group: 'Risk',    label: 'Suspend organizers' },
  { id: 'risk.blocklist',    group: 'Risk',    label: 'Add to blocklist' },
  { id: 'admin.users',       group: 'Admin',   label: `Manage ${BRAND.name} users` },
  { id: 'admin.roles',       group: 'Admin',   label: 'Edit role permissions' },
  { id: 'admin.audit',       group: 'Admin',   label: 'Access audit log' },
];
const ROLE_DEFAULTS = {
  'Operations Leads':  ['events.view','events.edit','events.publish','sales.pause','orders.comp','orders.manual','kyc.review','admin.audit'],
  'Finance Leads':     ['events.view','payouts.approve','payouts.override','finance.fees','admin.audit','orders.refund'],
  'Account Managers':  ['events.view','events.edit','orders.comp','kyc.review'],
  'Support Agents':    [],
};
const PERM_GROUPS = ['Events','Sales','Orders','Finance','Risk','Admin'];

export const PermissionEditorModal = ({ open, onClose, role, currentSummary }) => {
  const initial = ROLE_DEFAULTS[role] || [];
  const [enabled, setEnabled] = useState(initial);
  useEffect(() => { if (open) setEnabled(ROLE_DEFAULTS[role] || []); }, [open, role]);
  if (!open) return null;
  const toggle = (id) => setEnabled(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  const groups = PERM_GROUPS;

  return (
    <Modal open={open} onClose={onClose} width="max-w-[600px]" title={`Permissions · ${role || 'Role'}`} sub={currentSummary || 'Granular permission control for this role'}>
      <div className="space-y-3">
        {groups.map(g => (
          <div key={g} className="border border-[#E6E4E1] rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-[#FBFAF7] border-b border-[#E6E4E1] text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280]">{g}</div>
            <div className="divide-y divide-[#EEEDEA]">
              {PERMS.filter(p => p.group === g).map(p => {
                const on = enabled.includes(p.id);
                const isDangerous = p.id === 'sales.pause.all' || p.id === 'admin.roles' || p.id === 'events.cancel';
                return (
                  <label key={p.id} className="flex items-center justify-between px-3 py-2 hover:bg-[#FBFAF7] cursor-pointer">
                    <div className="min-w-0">
                      <div className={`text-[12px] ${isDangerous ? 'text-[#9B1F1F] font-medium' : 'text-[#1F2937]'}`}>{p.label}</div>
                      <div className="text-[10px] text-[#9CA3AF] font-mono mt-0.5"><IdTag value={p.id} /></div>
                    </div>
                    <button onClick={() => toggle(p.id)} className={`relative w-9 h-5 rounded-full flex-shrink-0 transition-colors ${on ? (isDangerous ? 'bg-[#9B1F1F]' : 'bg-[#1F2937]') : 'bg-[#E6E4E1]'}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-4' : ''}`} />
                    </button>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-md p-2.5 text-[11px] text-[#8A5A12] flex items-start gap-2">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Permission changes affect all users assigned to this role and take effect on their next session. All changes logged.</span>
        </div>
      </div>
      <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 sticky -bottom-4 z-10 flex items-center justify-between">
        <span className="text-[11.5px] text-[#6B7280]">{enabled.length} permission{enabled.length !== 1 ? 's' : ''} enabled</span>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
          {/* Toasted "Permissions saved" and wrote nothing: roles are defined in code (the backend's
              /api/roles says so — read-only by design), so there is nowhere to save these to. */}
          <Gate reason="Role permissions are defined in the platform’s code, so you can’t change them here — see them under Settings → Roles & permissions."><button className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] text-white">Save permissions</button></Gate>
        </div>
      </div>
    </Modal>
  );
};

